import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Card, CardContent, Typography, Chip, Alert, CircularProgress,
  Grid, IconButton, Tooltip, Avatar, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper
} from '@mui/material';
import {
  Refresh, Person, AdminPanelSettings, Storage, PhotoLibrary,
  VideoLibrary, Image as ImageIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { GlassmorphicCard, BodyText, SecondaryText } from '../styles/sharedStyles';

const StyledCard = styled(GlassmorphicCard)(({ theme }) => ({
  height: '100%',
  '&:hover': {
    transform: 'translateY(-5px)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4)',
  },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  background: theme.palette.background.paper,
  borderRadius: `${theme.custom?.cardRounding || 28}px`,
  backdropFilter: `blur(${theme.custom?.glassBlur || 16}px)`,
  border: '1px solid rgba(255, 255, 255, 0.06)',
  '& .MuiTableHead-root': {
    '& .MuiTableCell-head': {
      background: `${theme.palette.primary.main}1a`,
      color: theme.palette.primary.main,
      fontWeight: 700,
      fontSize: '0.875rem',
      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
      textTransform: 'uppercase',
      letterSpacing: '1px',
    },
  },
  '& .MuiTableBody-root': {
    '& .MuiTableRow-root': {
      '&:hover': {
        background: 'rgba(255, 255, 255, 0.02)',
      },
      '& .MuiTableCell-body': {
        color: '#ffffff',
        borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
        fontSize: '0.875rem',
      },
    },
  },
}));

const StatusChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'isAdmin'
})(({ isAdmin }) => ({
  background: isAdmin ? 'rgba(156, 39, 176, 0.1)' : 'rgba(0, 212, 255, 0.1)',
  color: isAdmin ? '#ce93d8' : '#00d4ff',
  border: `1px solid ${isAdmin ? 'rgba(156, 39, 176, 0.3)' : 'rgba(0, 212, 255, 0.3)'}`,
  fontWeight: 500,
}));

const ImmichUsers = ({ apiBaseUrl, token }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${apiBaseUrl}/api/immich/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(response.data || []);
    } catch (err) {
      console.error('Error fetching Immich users:', err);
      setError(err.response?.data?.detail || 'Failed to fetch Immich users');
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, token]);

  useEffect(() => {
    fetchUsers();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchUsers, 30000);
    return () => clearInterval(interval);
  }, [fetchUsers]);

  const formatBytes = (bytes) => {
    if (bytes === 0 || bytes === undefined || bytes === null) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getUserAvatarUrl = (user) => {
    if (user.profileImagePath) {
      return `${apiBaseUrl}/api/immich/profile-image/${user.id}`;
    }
    return null;
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  if (loading && users.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress sx={{ color: '#00d4ff' }} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600 }}>
          Immich Users ({users.length})
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchUsers} disabled={loading} sx={{ color: '#00d4ff' }}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>
          {error}
        </Alert>
      )}

      {users.length === 0 ? (
        <Box sx={{
          textAlign: 'center',
          py: 6,
          color: 'rgba(255, 255, 255, 0.7)'
        }}>
          <Typography variant="body1">
            {loading ? 'Loading users...' : 'No users found'}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {users.map((user) => (
            <Grid item xs={12} md={6} lg={4} key={user.id}>
              <StyledCard>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar
                      src={getUserAvatarUrl(user)}
                      sx={{ 
                        width: 56, 
                        height: 56, 
                        bgcolor: user.isAdmin ? '#9c27b0' : '#00d4ff',
                        fontSize: '1.25rem'
                      }}
                    >
                      {getInitials(user.name || user.email)}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600, fontSize: '1rem' }}>
                        {user.name || user.email}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.875rem' }}>
                        {user.email}
                      </Typography>
                    </Box>
                    <StatusChip
                      label={user.isAdmin ? 'Admin' : 'User'}
                      isAdmin={user.isAdmin}
                      size="small"
                      icon={user.isAdmin ? <AdminPanelSettings sx={{ fontSize: 16 }} /> : <Person sx={{ fontSize: 16 }} />}
                    />
                  </Box>

                  {user.storageUsage !== undefined && (
                    <Box sx={{ mb: 2 }}>
                      <SecondaryText sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                        Storage Usage
                      </SecondaryText>
                      <BodyText sx={{ fontSize: '0.875rem' }}>
                        {formatBytes(user.storageUsage)}
                      </BodyText>
                    </Box>
                  )}

                  {user.quotaSizeInBytes && (
                    <Box sx={{ mb: 2 }}>
                      <SecondaryText sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                        Quota
                      </SecondaryText>
                      <BodyText sx={{ fontSize: '0.875rem' }}>
                        {formatBytes(user.quotaSizeInBytes)}
                      </BodyText>
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                    {user.assetsCount !== undefined && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <ImageIcon sx={{ color: '#00d4ff', fontSize: 16 }} />
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                          {user.assetsCount} assets
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                    <SecondaryText sx={{ fontSize: '0.7rem' }}>
                      ID: {user.id}
                    </SecondaryText>
                    {user.createdAt && (
                      <SecondaryText sx={{ fontSize: '0.7rem' }}>
                        Created: {new Date(user.createdAt).toLocaleDateString()}
                      </SecondaryText>
                    )}
                  </Box>
                </CardContent>
              </StyledCard>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default ImmichUsers;
